import { LinkSimpleIcon, XIcon } from '@phosphor-icons/react'
import { fonts, mappedColors } from '@pixee/design-system'
import { createFormHook, createFormHookContexts, useSelector } from '@tanstack/react-form'
import classNames from 'classnames'
import { useCallback, useMemo, useRef } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import type { AreEquivalent, ItemType } from '../../types.ts'
import {
  useAllIntegrations,
  useGetRepositoryIntegrations,
  useSuspenseGetRepository,
} from '../../utils/api-client/api-hooks.ts'
import type { Repository, RepositoryIntegration, Tool } from '../../utils/api-client/api-schemas.ts'
import { ToolSchema } from '../../utils/api-client/api-schemas.ts'
import type { CreateSbomRequestFormData, CreateScanRequestFormData } from '../../utils/api-client/request-form-data.ts'
import { getSelectableTools, isAiScannerTool, isSbomTool } from '../../utils/detector-utils.ts'
import { convertToolToIntegrationType, getToolName } from '../../utils/repository-utils.tsx'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { InputContainer } from '../input/input-container'
import {
  getSelectableInstances,
  IntegrationInstanceSelector,
  type IntegrationInstanceSelection,
  type ToolIntegrationType,
} from '../input/integration-instance-selector.tsx'
import { RadioGroup, RadioItem } from '../input/radio'
import { SelectBox } from '../input/select-box.tsx'
import { TextInput } from '../input/text-input'
import { ToolIcon } from '../third-party-branding/tool-icon.tsx'
import {
  getIsSbomFileRequired,
  getToolIntegrationStatus,
  isGitLabTool,
  resolveWorkflowPath,
  validateCommitSha,
  type ToolIntegrationStatus,
} from './add-findings-drawer-utils.ts'
import * as styles from './add-findings-form.css.ts'

/** The form models "no tool" as `''`; the converter takes a `Tool`. One guard, so the two readers agree. */
const toIntegrationType = (tool: Tool | '') => (tool === '' ? undefined : convertToolToIntegrationType(tool))

export const ProvideMethodSchema = z.enum(['upload', 'import'])
export type ProvideMethod = z.infer<typeof ProvideMethodSchema>

export const RadioSelectionSchema = z.enum(['yes', 'no', ''])
type RadioSelection = z.infer<typeof RadioSelectionSchema>

export type AddFindingsFormProps = {
  repositoryId: string
}

export const AddFindingsForm: React.FC<AddFindingsFormProps> = ({ repositoryId }) => {
  const form = useFormContext()
  const values = useSelector(form.store, s => s.values)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // The mappings decide whether a tool that cannot fall back to a system default instance may be
  // imported at all, so the choice is resolved here rather than from the tool alone. Cached by
  // react-query, so this shares the request `ToolSelectField` already makes.
  const { data: repository } = useSuspenseGetRepository({ repositoryId })
  const { data: repositoryIntegrationsData } = useGetRepositoryIntegrations({ repositoryId })
  const selectedTool = values.selectedTool || undefined
  const toolIntegrationStatus = selectedTool
    ? getToolIntegrationStatus(selectedTool, repositoryIntegrationsData?._embedded?.items ?? [], repository.type)
    : undefined

  const workflowPath = resolveWorkflowPath(selectedTool, toolIntegrationStatus)
  const effectiveProvideMethod = resolveEffectiveProvideMethod(values)
  const isPixeeAiScanner = !!values.selectedTool && isAiScannerTool(values.selectedTool)

  const handleFileInputClear = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  return (
    <form className={styles.formContainer} onSubmit={event => event.preventDefault()}>
      <input type="hidden" name="repositoryId" value={repositoryId} />

      <ToolSelectField repositoryId={repositoryId} onFileInputClear={handleFileInputClear} />

      {workflowPath === 'choice' && <ProvideMethodField />}

      {effectiveProvideMethod === 'upload' && (
        <UploadWorkflowFields repositoryId={repositoryId} fileInputRef={fileInputRef} />
      )}

      {/* The scanner generates findings server-side for this repository, so it needs no file and no
          branch-import choice — those inputs would be ignored on submit. Show a short note instead. */}
      {isPixeeAiScanner ? (
        <PixeeAiScannerNote />
      ) : (
        effectiveProvideMethod === 'import' && <ImportWorkflowFields repositoryId={repositoryId} />
      )}
    </form>
  )
}

const PixeeAiScannerNote: React.FC = () => (
  <p style={{ font: fonts.small, color: mappedColors.neutral.neutral30, margin: 0 }}>
    Pixee will scan this repository and generate security findings for you — no file to upload. Use the toggle below to
    also triage and fix them automatically, then select <strong>Generate Findings</strong>.
  </p>
)

export type AddFindingsFooterActionsProps = {
  onCancel: () => unknown
  isPending: boolean
  onSubmit: (payload: SubmitPayload) => unknown
}

export const AddFindingsFooterActions: React.FC<AddFindingsFooterActionsProps> = ({
  onCancel,
  isPending,
  onSubmit,
}) => {
  const form = useFormContext()
  const values = useSelector(form.store, s => s.values)
  const { isSubmitDisabled, effectiveProvideMethod } = deriveFooterState(values)

  // The same list the selector reads, so the two share one request rather than opening two.
  const { integrations, isLoading: isLoadingIntegrations } = useAllIntegrations({ source: 'managed' })
  const selectedIntegrationType = toIntegrationType(values.selectedTool)

  const instanceCount = useMemo(
    () =>
      selectedIntegrationType === undefined ? 0 : getSelectableInstances(integrations, selectedIntegrationType).length,
    [integrations, selectedIntegrationType]
  )

  // Only an upload can go out unattributed; an import is routed by the repository's binding.
  const isAwaitingInstanceResolution =
    effectiveProvideMethod === 'upload' &&
    selectedIntegrationType !== undefined &&
    values.selectedIntegrationId === undefined &&
    isLoadingIntegrations

  // Also gated on the count: a tool with no instances connected has nothing to pick, and uploading
  // its findings unattributed stays legal.
  const isMissingRequiredInstance =
    effectiveProvideMethod === 'upload' && instanceCount > 0 && values.selectedIntegrationId === undefined

  const handleClick = () => {
    const payload = buildSubmitPayload(values)
    if (payload) {
      onSubmit(payload)
    }
  }

  const isPixeeAiScanner = !!values.selectedTool && isAiScannerTool(values.selectedTool)

  return (
    <>
      <GhostButton onClick={onCancel} type="secondary">
        Cancel
      </GhostButton>
      <DefaultButton
        buttonType="primary"
        onClick={handleClick}
        state={
          isSubmitDisabled || isAwaitingInstanceResolution || isMissingRequiredInstance
            ? 'disabled'
            : isPending
              ? 'loading'
              : 'default'
        }
      >
        {match({ isPixeeAiScanner, effectiveProvideMethod })
          .with({ isPixeeAiScanner: true }, () => 'Generate Findings')
          .with({ effectiveProvideMethod: 'import' }, () => 'Import')
          .otherwise(() => 'Upload')}
      </DefaultButton>
    </>
  )
}

const IntegrationStatusIcons: React.FC<{ status: ToolIntegrationStatus }> = ({ status }) => {
  if (status === 'none') {
    return null
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <LinkSimpleIcon size={16} weight="light" color={mappedColors.neutral.neutral30} />
    </div>
  )
}

const getToolSelectOption = (tool: Tool, integrations: RepositoryIntegration[], repositoryType: Repository['type']) => {
  const name = getToolName(tool)
  const status = getToolIntegrationStatus(tool, integrations, repositoryType)
  const isUnsupported = UNSUPPORTED_TOOLS.has(tool)
  return {
    value: tool,
    disabled: isUnsupported,
    hideDisabledCheckIcon: isUnsupported,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
        <ToolIcon tool={tool} size="medium" />
        <span className={styles.toolNameBold}>{name}</span>
        {!isUnsupported && <IntegrationStatusIcons status={status} />}
      </div>
    ),
  }
}

type ToolSelectFieldProps = {
  repositoryId: string
  onFileInputClear: () => unknown
}

const ToolSelectField: React.FC<ToolSelectFieldProps> = ({ repositoryId, onFileInputClear }) => {
  const { data: repository } = useSuspenseGetRepository({ repositoryId })
  const form = useFormContext()
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)
  const selectedIntegrationId = useSelector(form.store, s => s.values.selectedIntegrationId)

  const { data: integrationsData } = useGetRepositoryIntegrations({ repositoryId })
  const integrations = useMemo<RepositoryIntegration[]>(
    () => integrationsData?._embedded?.items ?? [],
    [integrationsData]
  )

  const handleSelectedToolChange = useCallback(
    (value: string) => {
      const parsed = ToolSchema.safeParse(value)
      if (!parsed.success) {
        console.warn(`Unknown tool value: "${value}"`, z.treeifyError(parsed.error))
      }
      form.reset()
      form.setFieldValue('selectedTool', parsed.success ? parsed.data : '')
      onFileInputClear()
    },
    [form, onFileInputClear]
  )

  // The tool is already chosen here, so the picker shows the instance field alone. Tools with no
  // integration of their own (an uploaded SARIF) have no instance to attribute and render nothing.
  const integrationType = toIntegrationType(selectedTool)

  const handleInstanceChange = useCallback(
    ({ integrationId }: IntegrationInstanceSelection) => form.setFieldValue('selectedIntegrationId', integrationId),
    [form]
  )

  /**
   * Who decides the instance depends on who fetches the findings.
   *
   * On an import Pixee pulls, and the platform routes that pull through the instance the repository
   * is bound to — a client-supplied `integration_id` does not change which credentials answer, and
   * the scan is recorded against whichever instance did. Offering a choice there would let a user
   * pick an instance the server will not use, so the field states the binding instead.
   *
   * On an upload the client supplies the findings, there is no pull, and `integration_id` is the
   * only thing that attributes them — so it is asked for, and required.
   */
  const isImport = useSelector(form.store, s => resolveEffectiveProvideMethod(s.values) === 'import')

  // `getSelectableTools` applies the Labs gate while keeping this drawer's order.
  const visibleTools = useMemo(() => getSelectableTools(TOOL_DISPLAY_ORDER), [])

  // Outside the Tool cluster's tight label gap, so it picks up the form's normal field spacing.
  return (
    <>
      <div className={styles.flexContainer}>
        <SelectBox
          label="Tool"
          name="tool"
          value={selectedTool}
          onValueChange={handleSelectedToolChange}
          placeholder="Select a tool"
          options={visibleTools.map(tool => getToolSelectOption(tool, integrations, repository.type))}
        />
      </div>
      {integrationType &&
        (isImport ? (
          <BoundInstanceField integrationType={integrationType} repositoryId={repositoryId} />
        ) : (
          <IntegrationInstanceSelector
            value={{ type: integrationType, integrationId: selectedIntegrationId }}
            onChange={handleInstanceChange}
            fixedType={integrationType}
            isInstanceRequired
          />
        ))}
    </>
  )
}

type BoundInstanceFieldProps = {
  integrationType: ToolIntegrationType
  /** Namespaces the field id, and is what this component fetches its own data with. */
  repositoryId: string
}

/**
 * States which instance an imported scan will be attributed to, read-only.
 *
 * The repository's mapping links to the instance the pull authenticates as (`_links.integration`),
 * so the binding is matched to the instance list by comparing HAL self links rather than by parsing
 * an id out of an href. A repository with no mapping, or one whose link names the tool's system
 * default, has no managed instance to name — the platform resolves the default at pull time.
 */
const BoundInstanceField: React.FC<BoundInstanceFieldProps> = ({ integrationType, repositoryId }) => {
  const { integrations } = useAllIntegrations({ source: 'managed' })
  // Same query key the parent already mounts, so react-query serves this from cache rather than
  // refetching — a component that mounts queries takes ids, not a sibling's unwrapped data.
  const { data: repositoryIntegrationsData } = useGetRepositoryIntegrations({ repositoryId })
  const repositoryIntegrations = useMemo(
    () => repositoryIntegrationsData?._embedded?.items ?? [],
    [repositoryIntegrationsData]
  )

  const boundInstanceName = useMemo(() => {
    const boundHref = repositoryIntegrations.find(integration => integration.type === integrationType)?._links
      ?.integration?.href
    if (boundHref === undefined) {
      return undefined
    }
    return integrations.find(instance => instance._links?.self?.href === boundHref)?.name
  }, [repositoryIntegrations, integrationType, integrations])

  return (
    <TextInput
      id={`boundInstance-${repositoryId}`}
      label="Instance"
      value={boundInstanceName ?? 'Default instance'}
      readOnly
      helperText="Pixee imports these findings using the instance this repository is connected to. Change it in the repository's settings."
    />
  )
}

const ProvideMethodField: React.FC = () => {
  const form = useFormContext()
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)
  const provideMethod = useSelector(form.store, s => s.values.provideMethod)

  if (selectedTool === '') {
    return null
  }

  return (
    <div className={styles.provideMethodContainer}>
      <span className={styles.provideMethodTitle}>How would you like to provide findings?</span>
      <RadioGroup
        value={provideMethod}
        onValueChange={value => form.setFieldValue('provideMethod', ProvideMethodSchema.parse(value))}
        className={styles.provideMethodRadioGroup}
      >
        <RadioItem value="upload" label="Upload a file" id="provide-upload" />
        <RadioItem value="import" label={`Import directly from ${getToolName(selectedTool)}`} id="provide-import" />
      </RadioGroup>
    </div>
  )
}

type UploadWorkflowFieldsProps = {
  repositoryId: string
  fileInputRef: React.RefObject<HTMLInputElement | null>
}

const UploadWorkflowFields: React.FC<UploadWorkflowFieldsProps> = ({ repositoryId, fileInputRef }) => {
  const form = useFormContext()
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)
  const file = useSelector(form.store, s => s.values.file)

  if (selectedTool === '') {
    return null
  }

  const fileName = file?.name ?? ''
  const showSbomUpload = isSbomTool(selectedTool)

  return (
    <>
      <FileInputField repositoryId={repositoryId} fileInputRef={fileInputRef} />
      {showSbomUpload && <SbomFileField repositoryId={repositoryId} />}
      {fileName !== '' && <UploadBranchQuestions repositoryId={repositoryId} />}
    </>
  )
}

type FileInputFieldProps = {
  repositoryId: string
  fileInputRef: React.RefObject<HTMLInputElement | null>
}

const FileInputField: React.FC<FileInputFieldProps> = ({ repositoryId, fileInputRef }) => {
  const form = useFormContext()
  const file = useSelector(form.store, s => s.values.file)
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)
  const fileName = file?.name ?? ''

  // CxSAST has no SARIF export, so its upload is the raw JSON its own API returns rather than the
  // SARIF every other upload tool here produces.
  const fileHelperText = match(selectedTool)
    .with(
      'checkmarx_enterprise',
      () => "The JSON object returned by CxSAST's /cxrestapi/sast/results endpoint. Example: cx_results.json"
    )
    .otherwise(() => 'Results are in SARIF format. Example: cx_results.sarif')

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue('file', event.target.files?.[0])
    },
    [form]
  )

  const handleClearFile = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation()
      form.setFieldValue('file', undefined)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [form, fileInputRef]
  )

  const handleFileKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLLabelElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        fileInputRef.current?.click()
      }
    },
    [fileInputRef]
  )

  return (
    <div className={styles.flexContainer}>
      <InputContainer id={`file-${repositoryId}`} label="File" helperText={fileHelperText}>
        <input
          ref={fileInputRef}
          type="file"
          id={`file-${repositoryId}`}
          name="file"
          hidden
          onChange={handleFileChange}
        />
        <label
          htmlFor={`file-${repositoryId}`}
          className={classNames(
            styles.fileInputField,
            fileName ? styles.fileInputFieldWithValue : styles.fileInputFieldPlaceholder
          )}
          tabIndex={0}
          onKeyDown={handleFileKeyDown}
        >
          {fileName ? (
            <>
              {fileName}
              <XIcon size={15} className={styles.closeIcon} onClick={handleClearFile} />
            </>
          ) : (
            'Select...'
          )}
        </label>
      </InputContainer>
    </div>
  )
}

/** Why an import still asks for a file: the pull brings findings across, but no SBOM. */
const IMPORT_SBOM_REASON = 'Pixee cannot fetch this from the tool, so it is needed even when findings are imported.'

const SbomFileField: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const form = useFormContext()
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)
  const sbomFile = useSelector(form.store, s => s.values.sbomFile)
  // A file input is expected on an upload and surprising on an import, so only the import explains
  // itself.
  const isImport = useSelector(form.store, s => resolveEffectiveProvideMethod(s.values) === 'import')
  const sbomFileName = sbomFile?.name ?? ''
  const sbomFileInputRef = useRef<HTMLInputElement>(null)

  const handleSbomFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      form.setFieldValue('sbomFile', event.target.files?.[0])
    },
    [form]
  )

  const handleSbomFileKeyDown = useCallback((event: React.KeyboardEvent<HTMLLabelElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      sbomFileInputRef.current?.click()
    }
  }, [])

  const { sbomLabel, sbomHelperText } = match(selectedTool)
    .with('gitlab_dependency_scanning', () => ({
      sbomLabel: 'SBOM Archive',
      sbomHelperText: 'GitLab dependency scanning archive. Example: archive.zip',
    }))
    // Black Duck Hub's other export formats fail silently rather than erroring: its CycloneDX 1.6
    // graph is truncated, so most packages are excluded as orphans, and its SPDX 3.0 export is
    // JSON-LD the parser reads as empty. Naming the one workable format here is the only warning
    // the customer gets.
    .with('black_duck_hub_sca', () => ({
      sbomLabel: 'SBOM File',
      sbomHelperText:
        "SPDX 2.3 only, from the same Black Duck project version. Black Duck's CycloneDX and SPDX 3.0 exports import incompletely. Example: sbom.spdx.json",
    }))
    .otherwise(() => ({
      sbomLabel: 'SBOM File',
      sbomHelperText: 'CycloneDX or SPDX SBOM file. Example: sbom.json',
    }))

  return (
    <div className={styles.flexContainer}>
      <InputContainer
        id={`sbomFile-${repositoryId}`}
        label={sbomLabel}
        helperText={isImport ? `${IMPORT_SBOM_REASON} ${sbomHelperText}` : sbomHelperText}
      >
        <input
          ref={sbomFileInputRef}
          type="file"
          id={`sbomFile-${repositoryId}`}
          name="sbomFile"
          hidden
          onChange={handleSbomFileChange}
        />
        <label
          htmlFor={`sbomFile-${repositoryId}`}
          className={classNames(
            styles.fileInputField,
            sbomFileName ? styles.fileInputFieldWithValue : styles.fileInputFieldPlaceholder
          )}
          tabIndex={0}
          onKeyDown={handleSbomFileKeyDown}
        >
          {sbomFileName || 'Select...'}
        </label>
      </InputContainer>
    </div>
  )
}

const UploadBranchQuestions: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const { data: repository } = useSuspenseGetRepository({ repositoryId })
  const defaultBranch = repository.default_branch
  const form = useFormContext()
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)
  const associateBranch = useSelector(form.store, s => s.values.associateBranch)
  const branch = useSelector(form.store, s => s.values.branch)
  const associateCommit = useSelector(form.store, s => s.values.associateCommit)
  const commitHash = useSelector(form.store, s => s.values.commitHash)
  const pipelineId = useSelector(form.store, s => s.values.pipelineId)

  const commitShaError = validateCommitSha(commitHash)
  const showGitLabPipelineId = selectedTool !== '' && isGitLabTool(selectedTool)

  return (
    <>
      <div className={styles.provideMethodContainer}>
        <span className={styles.provideMethodTitle}>
          Is the scan from the default branch{defaultBranch ? ` (${defaultBranch})` : ''}?
        </span>
        <RadioGroup
          value={associateBranch}
          onValueChange={value => {
            form.setFieldValue('associateBranch', RadioSelectionSchema.parse(value))
            form.setFieldValue('branch', '')
            form.setFieldValue('pipelineId', '')
          }}
          className={styles.provideMethodRadioGroup}
        >
          <RadioItem value="yes" label="Yes" id="associate-branch-yes" />
          <RadioItem value="no" label="No" id="associate-branch-no" />
        </RadioGroup>
      </div>

      {associateBranch === 'no' && (
        <div className={styles.flexContainer}>
          {showGitLabPipelineId ? (
            <TextInput
              id={`pipelineId-${repositoryId}`}
              label="Pipeline ID"
              name="pipelineId"
              value={pipelineId}
              onChange={value => form.setFieldValue('pipelineId', value)}
              placeholder="e.g. #123456789"
              explicitLabelCondition="optional"
            />
          ) : (
            <TextInput
              id={`branch-${repositoryId}`}
              label="Branch name"
              name="branch"
              value={branch}
              onChange={value => form.setFieldValue('branch', value)}
              placeholder="e.g. feature/my-branch"
            />
          )}
        </div>
      )}

      <div className={styles.provideMethodContainer}>
        <span className={styles.provideMethodTitle}>Is the scan from the head of the branch?</span>
        <RadioGroup
          value={associateCommit}
          onValueChange={value => {
            form.setFieldValue('associateCommit', RadioSelectionSchema.parse(value))
            form.setFieldValue('commitHash', '')
          }}
          className={styles.provideMethodRadioGroup}
        >
          <RadioItem value="yes" label="Yes" id="associate-commit-yes" />
          <RadioItem value="no" label="No" id="associate-commit-no" />
        </RadioGroup>
      </div>

      {associateCommit === 'no' && (
        <div className={styles.flexContainer}>
          <TextInput
            id={`commitHash-${repositoryId}`}
            label="Git commit hash"
            name="commitHash"
            value={commitHash}
            onChange={value => form.setFieldValue('commitHash', value)}
            placeholder="e.g. a1b2c3d4..."
            isError={!!commitShaError}
            errorText={commitShaError}
          />
        </div>
      )}
    </>
  )
}

const ImportWorkflowFields: React.FC<{ repositoryId: string }> = ({ repositoryId }) => {
  const { data: repository } = useSuspenseGetRepository({ repositoryId })
  const defaultBranch = repository.default_branch
  const form = useFormContext()
  const selectedTool = useSelector(form.store, s => s.values.selectedTool)
  const useDefaultBranch = useSelector(form.store, s => s.values.useDefaultBranch)
  const importBranch = useSelector(form.store, s => s.values.importBranch)
  const pipelineId = useSelector(form.store, s => s.values.pipelineId)

  if (selectedTool === '') {
    return null
  }

  return (
    <>
      {getIsSbomFileRequired(selectedTool) && <SbomFileField repositoryId={repositoryId} />}
      <div className={styles.provideMethodContainer}>
        <span className={styles.provideMethodTitle}>
          Import from the default branch{defaultBranch ? ` (${defaultBranch})` : ''}?
        </span>
        <RadioGroup
          value={useDefaultBranch}
          onValueChange={value => {
            form.setFieldValue('useDefaultBranch', RadioSelectionSchema.parse(value))
            form.setFieldValue('importBranch', '')
            form.setFieldValue('pipelineId', '')
          }}
          className={styles.provideMethodRadioGroup}
        >
          <RadioItem value="yes" label="Yes" id="default-branch-yes" />
          <RadioItem value="no" label="No" id="default-branch-no" />
        </RadioGroup>
      </div>

      {useDefaultBranch === 'no' && (
        <div className={styles.flexContainer}>
          {isGitLabTool(selectedTool) ? (
            <TextInput
              id={`pipelineId-import-${repositoryId}`}
              label="Pipeline ID"
              name="pipelineId"
              value={pipelineId}
              onChange={value => form.setFieldValue('pipelineId', value)}
              placeholder="e.g. #123456789"
            />
          ) : (
            <TextInput
              id={`importBranch-${repositoryId}`}
              label="Branch name"
              name="importBranch"
              value={importBranch}
              onChange={value => form.setFieldValue('importBranch', value)}
              placeholder="e.g. develop"
            />
          )}
        </div>
      )}
    </>
  )
}

// #region Form context

export type AddFindingsFormValues = {
  selectedTool: Tool | ''
  /** Undefined for a tool with no integration of its own, or before a choice has been made. */
  selectedIntegrationId: string | undefined
  provideMethod: ProvideMethod
  file: File | undefined
  sbomFile: File | undefined
  associateBranch: RadioSelection
  branch: string
  associateCommit: RadioSelection
  commitHash: string
  pipelineId: string
  useDefaultBranch: RadioSelection
  importBranch: string
}

const FORM_DEFAULT_VALUES: AddFindingsFormValues = {
  selectedTool: '',
  selectedIntegrationId: undefined,
  provideMethod: 'upload',
  file: undefined,
  sbomFile: undefined,
  associateBranch: 'yes',
  branch: '',
  associateCommit: 'yes',
  commitHash: '',
  pipelineId: '',
  useDefaultBranch: 'yes',
  importBranch: '',
}

const { fieldContext, formContext } = createFormHookContexts()

const { useAppForm, useTypedAppFormContext } = createFormHook({
  fieldComponents: {},
  formComponents: {},
  fieldContext,
  formContext,
})

export const useAddFindingsForm = (defaultValueOverrides: Partial<AddFindingsFormValues> = {}) =>
  useAppForm({
    defaultValues: { ...FORM_DEFAULT_VALUES, ...defaultValueOverrides },
  })

export const useFormContext = () => useTypedAppFormContext({ defaultValues: FORM_DEFAULT_VALUES })

// #endregion Form context

// #region Util helpers

/**
 * Display order for tools in the Add Findings drawer tool selector.
 * Must include every Tool value exactly once — enforced by the AreEquivalent check below.
 */
const TOOL_DISPLAY_ORDER = [
  'aikido_sast',
  'aikido_sca',
  'appscan',
  'arnica_sast',
  'polaris_sast',
  'polaris_sca',
  'black_duck_hub_sca',
  'checkmarx',
  'checkmarx_enterprise',
  'codeql',
  'contrast',
  'datadog_sast',
  'defectdojo',
  'dependabot',
  'fortify',
  'freeform',
  'gitlab_dependency_scanning',
  'gitlab_sast',
  'gitleaks',
  'pixee_ai',
  'semgrep',
  'snyk',
  'sonar',
  'veracode',
  'zap',
] satisfies readonly Tool[]
true satisfies AreEquivalent<ItemType<typeof TOOL_DISPLAY_ORDER>, Tool>

/**
 * Tools shown but not selectable in this drawer — kept visible (dimmed) to demonstrate
 * broader tool coverage. Specific to the Add Findings drawer; move to a shared location
 * only if another surface needs it.
 */
// Freeform findings are ingested through the Organization ▸ Findings intake tab, not added
// manually here, so the tool is shown (dimmed) but not selectable in this drawer.
const UNSUPPORTED_TOOLS: ReadonlySet<Tool> = new Set(['defectdojo', 'freeform'])

const resolveEffectiveProvideMethod = ({
  selectedTool,
  provideMethod,
}: Pick<AddFindingsFormValues, 'selectedTool' | 'provideMethod'>): ProvideMethod =>
  match(resolveWorkflowPath(selectedTool || undefined))
    .with('upload-only', () => 'upload' as const)
    .with('import-only', () => 'import' as const)
    .with('choice', undefined, () => provideMethod)
    .exhaustive()

type FooterState = {
  isSubmitDisabled: boolean
  effectiveProvideMethod: ProvideMethod
}

const deriveFooterState = (values: AddFindingsFormValues): FooterState => {
  const {
    selectedTool,
    file,
    sbomFile,
    associateBranch,
    branch,
    associateCommit,
    commitHash,
    pipelineId,
    useDefaultBranch,
    importBranch,
  } = values
  const effectiveProvideMethod = resolveEffectiveProvideMethod(values)

  // Upload-only: the commit-hash field is rendered nowhere else, and an import sends no `sha`. A
  // stale hash typed before switching would otherwise disable submit with nothing on screen to fix.
  const hasValidationErrors = effectiveProvideMethod === 'upload' && !!validateCommitSha(commitHash)

  const hasFile = !!file
  const isGitLabSelected = selectedTool !== '' && isGitLabTool(selectedTool)
  const hasUploadBranchSource = isGitLabSelected ? pipelineId !== '' : branch !== ''
  const hasBranch = associateBranch === 'yes' || hasUploadBranchSource
  const hasCommit = associateCommit === 'yes' || commitHash !== ''
  const isUploadReady = effectiveProvideMethod === 'upload' && hasFile && hasBranch && hasCommit

  const hasImportSource = isGitLabSelected ? pipelineId !== '' : importBranch !== ''
  // Without the file the scan lands with nothing to analyze it against.
  const hasRequiredImportSbom = !getIsSbomFileRequired(selectedTool || undefined) || !!sbomFile
  const isImportReady =
    effectiveProvideMethod === 'import' && (useDefaultBranch === 'yes' || hasImportSource) && hasRequiredImportSbom

  const isSubmitDisabled = selectedTool === '' || (!isUploadReady && !isImportReady) || hasValidationErrors
  return { isSubmitDisabled, effectiveProvideMethod }
}

export type SubmitPayload = {
  scanBody: CreateScanRequestFormData
  sbomBody?: CreateSbomRequestFormData
  effectiveProvideMethod: ProvideMethod
}

const buildSubmitPayload = (values: AddFindingsFormValues): SubmitPayload | null => {
  const {
    selectedTool,
    selectedIntegrationId,
    file,
    sbomFile,
    associateBranch,
    branch,
    associateCommit,
    commitHash,
    useDefaultBranch,
    importBranch,
    pipelineId,
  } = values
  if (!selectedTool) {
    return null
  }

  const effectiveProvideMethod = resolveEffectiveProvideMethod(values)
  const isUpload = effectiveProvideMethod === 'upload'

  const branchValue = match({ isUpload, associateBranch, useDefaultBranch })
    .with({ isUpload: true, associateBranch: 'no' }, () => branch || undefined)
    .with({ isUpload: false, useDefaultBranch: 'no' }, () =>
      isGitLabTool(selectedTool) ? undefined : importBranch || undefined
    )
    .otherwise(() => undefined)
  const shaValue = isUpload && associateCommit === 'no' ? commitHash || undefined : undefined

  const scanBody: CreateScanRequestFormData = {
    metadata: {
      tool: selectedTool,
      sha: shaValue,
      branch: branchValue,
      workflow_execution_policy: 'execute',
      gitlab_pipeline_id: isGitLabTool(selectedTool) && pipelineId ? Number(pipelineId) : undefined,
      // Ignored on an import — the server records whichever instance its pull authenticated as, so
      // sending one it overrides invites a report that contradicts the request.
      integration_id: isUpload ? selectedIntegrationId : undefined,
    },
  }

  if (isUpload && file) {
    scanBody.files = { [file.name]: file }
  }

  // Either the customer supplied the file or the platform can read one; a fileless request for
  // anything else is rejected, so it is not sent at all.
  const sbomBody: CreateSbomRequestFormData | undefined =
    isSbomTool(selectedTool) && (sbomFile || (!isUpload && !getIsSbomFileRequired(selectedTool)))
      ? { metadata: { tool: selectedTool, sha: shaValue, branch: branchValue }, file: sbomFile }
      : undefined

  return { scanBody, sbomBody, effectiveProvideMethod }
}

// #endregion Util helpers
